import { PlusOutlined } from '@ant-design/icons';
import {
  DrawerHeaderAction,
  EmailSendModal,
  InspectionFormModal,
  InspectionPickerDrawer,
  SharedEmpty,
  SharedTable,
} from '@components';
import { IInspectionsHook } from '@models';
import { Alert, Button } from 'antd';
import React, { useState } from 'react';
import { Columns } from './OrderInspectionTable.columns';
import { OrderInspectionPrintModal } from './OrderInspectionPrintModal';
import './OrderInspectionTable.css';

interface TableProps {
  open: boolean;
  readOnly?: boolean;
  inspectionId: string | null;
  inspectionsHook: IInspectionsHook;
  pickerOpen: boolean;
  addedCategoryIds: number[];
  vhcEmailModalOpen: boolean;
  vhcEmail: string;
  vhcEmailError: string | null;
  vhcEmailSending: boolean;
  handleClose: () => void;
  handleOpen: (id: string) => void;
  handlePublish: (id: string) => void;
  handleVhcEmailOpen: (id: string) => void;
  handleAddInspection: (categoryId: number) => Promise<void>;
  handleDeleteInspection: (categoryId: number) => void;
  setPickerOpen: (open: boolean) => void;
  handleVhcEmailChange: (val: string) => void;
  handleVhcEmailSend: () => void;
  handleVhcEmailCancel: () => void;
}

const OrderInspectionTable: React.FC<TableProps> = ({
  open,
  readOnly,
  inspectionId,
  inspectionsHook,
  pickerOpen,
  addedCategoryIds,
  vhcEmailModalOpen,
  vhcEmail,
  vhcEmailError,
  vhcEmailSending,
  handleClose,
  handleOpen,
  handlePublish,
  handleVhcEmailOpen,
  handleAddInspection,
  handleDeleteInspection,
  setPickerOpen,
  handleVhcEmailChange,
  handleVhcEmailSend,
  handleVhcEmailCancel,
}) => {
  const [printInspectionId, setPrintInspectionId] = useState<string | null>(null);

  return (
    <>
      {!readOnly && (
        <>
          {/* Adding goes through a picker drawer, like Add work and Log time, so the table is the one
              list of what is on the job - the old multi-select showed every row a second time as a
              chip above it. */}
          <DrawerHeaderAction>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>
              Add inspection
            </Button>
          </DrawerHeaderAction>
          <Alert
            message="Publishing inspection will make it visible to the mobile app user and automatically generate an internal service request for the garage manager to review and process."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <InspectionPickerDrawer
            open={pickerOpen}
            addedIds={addedCategoryIds}
            onClose={() => setPickerOpen(false)}
            onAdd={(category) => handleAddInspection(category.id)}
          />
        </>
      )}
      {/* An empty table - headers, scrollbars and all - reads as broken; the empty state says it. */}
      {!inspectionsHook.loading && inspectionsHook.dataSource.length === 0 ? (
        <SharedEmpty
          description={
            readOnly
              ? 'No inspections on this job'
              : 'No inspections on this job yet - use Add inspection to add one'
          }
        />
      ) : (
        <div className="order-inspection-table">
          <SharedTable
            {...inspectionsHook}
            // A job carries four or five inspections at most; a pager under them is only noise. The
            // vehicle-wide history (read-only) spans every job, so that one keeps it.
            pagination={readOnly ? undefined : false}
            columns={Columns({
              handleEdit: handleOpen,
              handlePublish,
              handleVhcEmailOpen,
              handlePrint: (id) => setPrintInspectionId(id),
              handleRemove: readOnly ? undefined : handleDeleteInspection,
            })}
          />
        </div>
      )}
      {inspectionId && (
        <InspectionFormModal open={open} onCancel={handleClose} inspectionId={inspectionId} />
      )}
      <OrderInspectionPrintModal
        inspectionId={printInspectionId}
        onDone={() => setPrintInspectionId(null)}
      />
      <EmailSendModal
        open={vhcEmailModalOpen}
        title="Send inspection email"
        emailValue={vhcEmail}
        setEmailValue={handleVhcEmailChange}
        emailError={vhcEmailError}
        onConfirm={handleVhcEmailSend}
        onCancel={handleVhcEmailCancel}
        confirmText="Send"
        okButtonProps={{ disabled: !!vhcEmailError, loading: vhcEmailSending }}
      />
    </>
  );
};

export default OrderInspectionTable;
